/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { CampaignCompanyDownloadResponse } from '../models/CampaignCompanyDownloadResponse';
import type { CampaignDetailResponse } from '../models/CampaignDetailResponse';
import type { CampaignRegisterForm } from '../models/CampaignRegisterForm';
import type { CampaignRegisterResponse } from '../models/CampaignRegisterResponse';
import type { CampaignShopDownloadResponse } from '../models/CampaignShopDownloadResponse';
import type { ChangeEndDateForm } from '../models/ChangeEndDateForm';
import type { GenerateShopFileUploadItemsResponse } from '../models/GenerateShopFileUploadItemsResponse';
import type { PageCampaignIndexResponse } from '../models/PageCampaignIndexResponse';
import type { ShopRegisterErrorCsvDownloadResponse } from '../models/ShopRegisterErrorCsvDownloadResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class CampaignRepository {

  /**
   * キャンペーン一覧表示
   * @returns PageCampaignIndexResponse OK
   * @throws ApiError
   */
  public static index({
    publicName,
    name,
    statuses,
    approvalStatuses,
    walletServiceIds,
    companyIds,
    shopIds,
    applicantName,
    approverName,
    actionTypes,
    startDateTime,
    endDateTime,
    page = 1,
    size = 20,
    sort,
  }: {
    /**
     * キャンペーン公開名称
     */
    publicName: string | undefined,
    /**
     * キャンペーン名
     */
    name: string | undefined,
    /**
     * ステータス
     */
    statuses: Array<'NEW' | 'WAITING' | 'IN_PROGRESS' | 'FINISH' | 'CANCEL'> | undefined,
    /**
     * 承認ステータス
     */
    approvalStatuses: Array<'WAITING' | 'CHANGE_WAITING' | 'CANCEL_WAITING' | 'REJECT' | 'WITHDRAWAL' | 'APPROVE' | 'CANCEL' | 'FAILURE'> | undefined,
    /**
     * ウォレットサービスID
     */
    walletServiceIds: Array<string> | undefined,
    /**
     * 企業ID
     */
    companyIds: Array<string> | undefined,
    /**
     * 店舗ID
     */
    shopIds: Array<string> | undefined,
    /**
     * 登録者
     */
    applicantName: string | undefined,
    /**
     * 最終承認者
     */
    approverName: string | undefined,
    /**
     * アクション区分
     */
    actionTypes: Array<'COUNTER_CHARGE' | 'BANK_ACCOUNT_CHARGE' | 'CREDIT_CARD_CHARGE' | 'PREPAID_SETTLEMENT' | 'DIRECT_BANK_SETTLEMENT'> | undefined,
    /**
     * キャンペーン開始日時
     */
    startDateTime: string | undefined,
    /**
     * キャンペーン終了日時
     */
    endDateTime: string | undefined,
    /**
     * One-based page index (1..N)
     */
    page: number | undefined,
    /**
     * The size of the page to be returned
     */
    size: number | undefined,
    /**
     * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
     */
    sort: Array<string> | undefined,
  }): CancelablePromise<PageCampaignIndexResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/campaign',
      query: {
        'publicName': publicName,
        'name': name,
        'statuses': statuses,
        'approvalStatuses': approvalStatuses,
        'walletServiceIds': walletServiceIds,
        'companyIds': companyIds,
        'shopIds': shopIds,
        'applicantName': applicantName,
        'approverName': approverName,
        'actionTypes': actionTypes,
        'startDateTime': startDateTime,
        'endDateTime': endDateTime,
        'page': page,
        'size': size,
        'sort': sort,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * キャンペーン登録
   * @returns CampaignRegisterResponse OK
   * @throws ApiError
   */
  public static register({
    requestBody,
  }: {
    /**
     * キャンペーン登録情報
     */
    requestBody: CampaignRegisterForm,
  }): CancelablePromise<CampaignRegisterResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/campaign',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * ファイル名＆署名付きURL生成
   * @returns GenerateShopFileUploadItemsResponse OK
   * @throws ApiError
   */
  public static generate(): CancelablePromise<GenerateShopFileUploadItemsResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/campaign/generate',
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * キャンペーン詳細表示
   * @returns CampaignDetailResponse OK
   * @throws ApiError
   */
  public static detail({
    id,
  }: {
    /**
     * キャンペーンID
     */
    id: string,
  }): CancelablePromise<CampaignDetailResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/campaign/{id}',
      path: {
        'id': id,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * キャンペーン終了日時変更
   * @returns CampaignDetailResponse OK
   * @throws ApiError
   */
  public static changeEndDate({
    id,
    requestBody,
  }: {
    /**
     * キャンペーンID
     */
    id: string,
    requestBody: ChangeEndDateForm,
  }): CancelablePromise<CampaignDetailResponse> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/campaign/{id}',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * キャンペーン承認
   * @returns CampaignDetailResponse OK
   * @throws ApiError
   */
  public static approve({
    id,
  }: {
    /**
     * キャンペーンID
     */
    id: string,
  }): CancelablePromise<CampaignDetailResponse> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/campaign/{id}/approve',
      path: {
        'id': id,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * キャンペーン取消
   * @returns CampaignDetailResponse OK
   * @throws ApiError
   */
  public static cancel({
    id,
  }: {
    /**
     * キャンペーンID
     */
    id: string,
  }): CancelablePromise<CampaignDetailResponse> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/campaign/{id}/cancel',
      path: {
        'id': id,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 対象企業Csvを生成、ストレージにUploadし、ダウンロードするための署名付きURLを返却する
   * @returns CampaignCompanyDownloadResponse OK
   * @throws ApiError
   */
  public static downloadCompany({
    id,
  }: {
    /**
     * キャンペーンID
     */
    id: string,
  }): CancelablePromise<CampaignCompanyDownloadResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/campaign/{id}/download/company',
      path: {
        'id': id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 店舗登録エラーCsvをダウンロードするための署名付きURLを返却する
   * @returns ShopRegisterErrorCsvDownloadResponse OK
   * @throws ApiError
   */
  public static downloadError({
    id,
  }: {
    /**
     * キャンペーンID
     */
    id: string,
  }): CancelablePromise<ShopRegisterErrorCsvDownloadResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/campaign/{id}/download/error',
      path: {
        'id': id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * 対象店舗Csvを生成、ストレージにUploadし、ダウンロードするための署名付きURLを返却する
   * @returns CampaignShopDownloadResponse OK
   * @throws ApiError
   */
  public static downloadShop({
    id,
  }: {
    /**
     * キャンペーンID
     */
    id: string,
  }): CancelablePromise<CampaignShopDownloadResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/campaign/{id}/download/shop',
      path: {
        'id': id,
      },
      errors: {
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * キャンペーン却下
   * @returns CampaignDetailResponse OK
   * @throws ApiError
   */
  public static reject({
    id,
  }: {
    /**
     * キャンペーンID
     */
    id: string,
  }): CancelablePromise<CampaignDetailResponse> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/campaign/{id}/reject',
      path: {
        'id': id,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

  /**
   * キャンペーン申請取下げ
   * @returns CampaignDetailResponse OK
   * @throws ApiError
   */
  public static withdraw({
    id,
  }: {
    /**
     * キャンペーンID
     */
    id: string,
  }): CancelablePromise<CampaignDetailResponse> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/campaign/{id}/withdraw',
      path: {
        'id': id,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        404: `Not Found`,
        500: `Internal Server Error`,
      },
    });
  }

}
